import {useState, useCallback, useMemo, useRef, useEffect} from "react";
import {backgroundLabel} from "libs/bgNumbering";
import {useBackgroundNumbers} from "hooks/useBackgroundNumbers";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useSetting, PIXELATION_SCENE_FIELDS} from "contexts/setting";
import {API} from "api";
import {useCurrentSet} from "hooks/useCurrentSet";
import {TEXT_RECORD_FIELDS} from "contexts/edits";
import {useDefaultSceneOverrides, useSetLocalOverride} from "hooks/useDefaultSceneOverrides";
import {useBgDerivation} from "hooks/useBgDerivation";
import {isDraftUploadVariation} from "libs/bgIdentity";
import {sessionBgScope, useSessionBgUploads} from "libs/sessionBgUploads";
import {usePromoteUpload} from "hooks/usePromoteUpload";
import {mergeScenes} from "utils/mergeScenes";
import {withoutInstanceAppearance} from "utils/allProductsScene";
import {getReleaseProductCodesForType} from "utils/releases";
import {drawnTemplates} from "data/templateSlots";
import {useTemplateSelection} from "hooks/useTemplateSelection";
import {variantsForLayout} from "data/products/index";
import {VISIBILITY_CATALOG_FIELD, withVisibilityCatalog} from "data/defaultSceneVisibility";
import {invalidatedQueryKeys} from "./invalidatedQueryKeys.ts";
import {
  CROP_FIELDS,
  backgroundSlotTarget,
  cropTargetLabel,
  shadowedCropTarget,
  templateImageKeys,
} from "./cropRowTarget.ts";
import {isGalleryOption, rasterUploadId} from "layout/BgTransformer/templateBg";
import {BG_PREVIEW_TEMPLATE_FIELD} from "libs/bgPreviewTemplate";
import {getRouteTemplates} from "utils/routeTemplates";
import {lastAdjustedCandidate, previewCandidates} from "../BgSelector/tilePreviewGeometry";
import {useImages} from "contexts/images";
import {useBgTransformContext} from "contexts/bgTransform";
import {rowAddressLabel, shadowingRowPatches} from "./shadowingRows.ts";
import {BG_COLOUR_FIELDS, LOOK_ROW_FIELDS, lookRowTarget} from "./lookRowTarget.ts";
import StageOrderSection from "./StageOrderSection.tsx";
import SelectorVisibilitySection from "./SelectorVisibilitySection.tsx";
import BannerCompositionSection from "./BannerCompositionSection.tsx";
import TextColorSection from "./TextColorSection.tsx";
import ReplicateBgSection from "./ReplicateBgSection.tsx";
import {canReplicateOnChannel} from "./replicateBg.ts";
import {
  BACKGROUND_BOUND_FIELDS,
  defaultStageOptions,
  isBackgroundLevel,
  resolveTargetStages,
  withoutBackgroundBoundFields,
} from "./stageTargets.ts";
import {useFeatures} from "hooks/useFeature";
import {useChannelSettings} from "hooks/useChannelSettings";
import {getBgOps} from "libs/bgOps";
import {
  BAKED_BG_CHANNELS,
  bakeRowAddress,
  bakeRowPatch,
  collectBakeJobs,
  runBakeJobs,
} from "./bakeOnSave.ts";
import "./index.css";

/** The crop fields, plus the preview banner that travels with them. */
const CROP_PATCH_FIELDS = [...CROP_FIELDS, BG_PREVIEW_TEMPLATE_FIELD];

// Every text record the editor holds, read from the edits context: a list kept
// here by hand left out names2-6, positions2 and times, so Save dropped them.
const TEXT_FIELDS = [...TEXT_RECORD_FIELDS, "gdnTagOrder", "tags"];

/**
 * Scene fields that a default scene does not carry, and why. Every other field
 * of the saved scene must be in FIELD_GROUPS (a unit test checks this).
 */
export const NOT_DEFAULT_FIELDS = Object.freeze({
  product: "row key",
  channel: "row key",
  type: "row key",
  // A detached chain is how an admin edits one banner. A preset applies with
  // the chain intact (see the "visibility" group).
  locked: "editing state",
  // The mode table is never a default: per-template values load as presets
  // (libs/sceneScope.ts presetModesFromScene).
  templateModes: "derived on load",
  // Built from the per-template records when a default loads (templateBg.ts).
  bgPresets: "derived on load",
  // Marks an image the user uploaded into one detached banner in this
  // session. A saved image is that banner's image for the background, chain
  // intact, however it got there (templateBg.ts): carried into a default, the
  // mark hid it until the banner was detached again.
  customBgUploaded: "editing state",
  // Baked backgrounds are cut from what the page shows when a default is
  // saved (bakeOnSave.ts); the loaded ones are never copied back.
  bakedBgs: "written by the bake on save",
  bakedBgOption: "derived on load",
  // The designs a saved scene keeps (data/templateSlots.ts). A default
  // follows the designs on air; the server refuses the field in a row.
  templateMembers: "saved scenes only",
  // The code highlight a user placed over a screenshot (libs/codeHighlight.ts).
  // Its default is the design's box in the product's colour.
  codeHighlights: "saved scenes only",
  fieldColors: "Grid v2 appearance; not offered by the Defaults panel",
  elementColors: "Grid v2 appearance; not offered by the Defaults panel",
});

export const FIELD_GROUPS = [
  {
    key: "texts",
    label: "Texts",
    fields: TEXT_FIELDS,
  },
  {
    key: "transforms",
    label: "Transforms",
    fields: [
      "bgX",
      "bgY",
      "bgScale",
      "bgRotate",
      "bgFlip",
      "bgOpacities",
      "bgBrightnesses",
      "uploadX",
      "uploadY",
      "uploadScale",
      "uploadRotate",
      "uploadFlip",
      "logoX",
      "logoY",
      "logoScale",
      "customBg",
    ],
  },
  {
    key: "templateBackgrounds",
    label: "Per-template backgrounds",
    // The per-template customBgs record: Livestream's inline gradient SVGs,
    // and on every channel the image an admin gave one banner while the rest
    // follow the gallery — saved here, it becomes that banner's preset for
    // this background (see layout/BgTransformer/templateBg.ts).
    fields: ["customBgs"],
  },
  {
    key: "images",
    label: "Images",
    fields: [
      "uploadImages",
      "logos",
      "logoVariants",
      "textColorLocked",
      "buttonColors",
      "logoHidden",
    ],
  },
  {
    key: "settings",
    label: "Settings",
    fields: [
      "bgOption",
      // The two design axes travel together: a product whose banner ships in
      // brand treatments (JetBrains Air) picks a look as one (layout, variant)
      // cell, so a default that carries the layout must carry the treatment or
      // it lands a pair the design never drew. The merge already accepts it —
      // `variantOption` is a scalar field server-side (api/v3/resolve-defaults)
      // and in utils/mergeScenes — only this list used to leave it out.
      "layoutOption",
      "variantOption",
      "fontOption",
      "iconOption",
      "isFontToggle",
      "isButtonEnabled",
      "gdnColorVariants",
      "gdnAccentColor",
      "isUppercase",
      "base",
      "fontVariation",
      "allProductsBgOptions",
      "titleFontSize",
      "generativeGradients",
      "generativeSpeed",
      "generativeCount",
      "generativeStops",
      // The generated gradients carry a palette key with this value in it: it
      // must travel with them, or the default regenerates on load.
      "regenerateKey",
      // Per-template layout, variant and base colour (scene scopes). The mode
      // table itself is never a default: everything per-template a default
      // carries loads as a preset (libs/sceneScope.ts presetModesFromScene).
      "layouts",
      "variants",
      "bases",
    ],
  },
  {
    key: "pixelation",
    label: "Pixelation",
    // `pixelationEnabled` (the on/off gate) is deliberately NOT part of
    // PIXELATION_SCENE_FIELDS — App.buildScene keeps it separate so it can
    // prune the knobs when the overlay is off (see prunePixelationForSave).
    // It must be listed here explicitly, otherwise default scenes would save
    // every pixelation knob but silently drop the on/off state (COART-1465).
    fields: ["pixelationEnabled", ...PIXELATION_SCENE_FIELDS],
  },
  {
    key: "visibility",
    label: "Visibility",
    // `locked` is deliberately not saved: a detached chain is how an admin
    // EDITS one banner, not part of what users should inherit — a preset
    // applies with the chain intact. The page's banners (Banners section,
    // libs/bannerComposition.ts) travel with the eyes and the order.
    fields: ["visible", "order", "hiddenTemplates", "extraTemplates"],
  },
];

// `layout-background` keys a row by both: what one layout changes about one
// background (the Air screenshot layout moves the artwork), merged on top of
// the background's own row by the cascade (hooks/useDefaultSceneOverrides.js).
//
// `variant` and `layout-variant` are the brand-treatment axis. A product whose
// banner ships in treatments (JetBrains Air) picks its look as one (layout,
// variant) cell, and the cell is the only row that can hold what the design
// draws for it alone — a layout row would land the same values on every
// treatment. Both are offered only where the product HAS treatments.
const BASE_LEVELS = [
  {value: "set"},
  {value: "layout"},
  {value: "background"},
  {value: "layout-background"},
];

const VARIANT_LEVELS = [{value: "variant"}, {value: "layout-variant"}];

const VARIANT_LEVEL_VALUES = new Set(VARIANT_LEVELS.map((item) => item.value));

/** The levels a product offers: the treatment ones only where it has them. */
export function levelsForVariants(hasVariants) {
  return hasVariants ? [...BASE_LEVELS, ...VARIANT_LEVELS] : BASE_LEVELS;
}

const ALL_PRODUCTS_BG_OPTIONS_FIELD = "allProductsBgOptions";
const FIELD_LABELS = {
  [ALL_PRODUCTS_BG_OPTIONS_FIELD]: "galleryVariant",
  hiddenTemplates: "removedBanners",
  extraTemplates: "bannersFromOtherSets",
};

// The names of the fields that go to the row of the background, in the notes.
const LOOK_ROW_FIELD_NAMES = {
  bgBrightness: "brightness",
  bgOpacity: "opacity",
  base: "base colour",
  bases: "base colour",
  textColorLocked: "text colour",
  logoVariants: "logo",
};

/** "a", "a and b", "a, b and c". */
function joinNames(names) {
  return names.length < 2 ? names.join("") : `${names.slice(0, -1).join(", ")} and ${names.at(-1)}`;
}

// Metadata fields that must always be included for a valid scene
const META_FIELDS = ["product", "channel", "type", "set"];

// Visual-polish fields users almost never want to opt out of saving. Lifted
// out of the "Settings" group so a stale group toggle (or a first save with
// the group unchecked) can't silently drop them from the override — which
// previously caused users to press Save and reload to find the brightness
// they'd just tuned was gone. Tracked under a deeper UI-model rework in #367.
const ALWAYS_INCLUDED_FIELDS = ["bgBrightness", "bgBlurValue", "bgOpacity"];

export function getFieldGroups(channelValue) {
  return FIELD_GROUPS.filter(
    (group) => !group.channels || group.channels.includes(channelValue)
  );
}

export function getAllFieldSelection(checked, channelValue) {
  return checked
    ? Array.from(new Set(getFieldGroups(channelValue).flatMap((group) => group.fields)))
    : [];
}

function normalizeScopeValue(value) {
  if (value === undefined || value === null || value === "") return null;
  return String(value);
}

function matchesOverrideRow(row, {channel, product, type, set, layout, background, variant}) {
  return (
    row.channel === channel &&
    row.product === product &&
    row.type === type &&
    normalizeScopeValue(row.set) === normalizeScopeValue(set) &&
    normalizeScopeValue(row.layout) === normalizeScopeValue(layout) &&
    normalizeScopeValue(row.background) === normalizeScopeValue(background) &&
    normalizeScopeValue(row.variant) === normalizeScopeValue(variant)
  );
}

/** One row's address, for telling whether two writes hit the same row. */
function rowKey({product, layout, background, variant}) {
  return [product, layout, background, variant].map((value) => value || "").join(":");
}

// The page's resolved defaults. A banner change marks them stale and does not
// fetch them again: the page would apply its default again, and that undoes
// the changes on it that nobody saved.
const RESOLVED_DEFAULT_QUERY_KEYS = [["v3-resolved-default"], ["v3-resolved-default-all"]];

/**
 * The templates a Visibility save records as known: the ones the row knew
 * before and the ones the channel draws now. The list only grows, so the hide
 * of a design that is not on air at save time survives until it comes back
 * (data/templateSlots.ts).
 */
export function visibilityCatalog(channel, selection, previousSceneData) {
  const previous = previousSceneData?.[VISIBILITY_CATALOG_FIELD];
  const drawn = drawnTemplates(channel, selection).map(({id}) => id);
  return [...new Set([...(Array.isArray(previous) ? previous : []), ...drawn])];
}

// Exported for unit tests — see __tests__/pickFields.test.js
export function pickFields(scene, fieldKeys, currentTemplateIds) {
  const allowedFields = new Set([...META_FIELDS, ...ALWAYS_INCLUDED_FIELDS]);
  for (const fieldKey of fieldKeys) {
    allowedFields.add(fieldKey);
  }
  const result = {};
  for (const key of Object.keys(scene)) {
    if (allowedFields.has(key)) {
      result[key] = scene[key];
    }
  }
  return withVisibilityCatalog(result, currentTemplateIds);
}

function shortenSetName(name) {
  const value = name || "default";
  if (value.length <= 8) return value;
  return `${value.slice(0, 8)}...`;
}

const DefaultScenePanel = ({
  buildScene,
  isChannelReady = true,
  isRouteDefaultReady = true,
  // The gallery's tiles, for the visibility setting (SelectorVisibilitySection).
  bgDisplayOrder = null,
  allBgUrls = null,
  expanded,
}) => {
  // Role features (data/features.ts); each one requires the capability that
  // its section's API checks.
  const features = useFeatures();
  // The defaults an admin edits belong to the offering, not to the way in: a
  // product with a home scope (JetBrains Air) keeps one set of rows for every
  // channel that lists it. `dataChannelObj` / `dataTypeObj` name that scope
  // and equal `channel` / `type` for every other product.
  const {
    dataChannelObj: channel,
    dataTypeObj: type,
    product,
    layoutOption,
    variantOption,
    bgOption,
    perProductBg,
    visible,
    hiddenTemplates,
    extraTemplates,
    base,
    bases,
  } = useSetting();
  const {
    bgAdjustmentOrder,
    customBg,
    customBgs,
    editCustomBg,
    pendingUploadCount,
    bgBrightness,
    bgOpacity,
    textColorLocked,
    logoVariants,
  } = useImages();
  // Phase-2 (issue #528, 2c): when on, save via PATCH (server merges only the
  // selected fields — 1d) and converge via query invalidation instead of the
  // client-side merge + hand-patched cache (useSetLocalOverride). Default off.
  const bgDerivation = useBgDerivation();
  // A save reads the page. Before the default is applied, and before the
  // background positions of the layout and background on screen have loaded,
  // the page shows something else: a save then writes it over the default.
  const {transformsReady} = useBgTransformContext();
  const isPageReadyToSave = isRouteDefaultReady && transformsReady;

  // A default-scene row is keyed by the background's identity (its catalog
  // variation), so a reorder never moves it to another image. A draft upload
  // has only a temporary identity until the list is published: a row saved
  // for it would point at nothing afterwards, so saving is blocked.
  const bgIsDraftUpload = isDraftUploadVariation(bgOption);
  // A temporary upload tile (libs/sessionBgUploads.ts) is saved by first making
  // it the product's FIRST background for everyone, then saving as usual; an
  // admin's list draft still has to be published from the list editor. On a
  // releases page only the set on screen gets the image, in the gallery of the
  // stage on screen: the save is for this set and stage (Sergey, 2026-10-27).
  const sessionScope = sessionBgScope(channel.value, type.value, product.value);
  const sessionUploads = useSessionBgUploads(sessionScope);
  const sessionUpload = bgIsDraftUpload
    ? (sessionUploads.find((upload) => String(upload.variation) === String(bgOption)) ?? null)
    : null;
  // A saved background is a tile, never the custom slot (Sergey, 2026-10-16).
  // An uploaded image the page shows in the custom slot — a default saved
  // before uploads became tiles opens one so — is made the first background
  // first, as a temporary upload tile is. SVG and video keep the custom slot,
  // and so do Ads and the ALL view, which have no tile for it.
  const customUploadId =
    String(bgOption) === "custom" && channel.value !== "ads" && product.value !== "ALL"
      ? rasterUploadId(customBg)
      : null;
  const uploadToPromote = sessionUpload?.uploadId ?? customUploadId;
  const [promotedTo, setPromotedTo] = useState(null);
  const queryClient = useQueryClient();
  const currentSet = useCurrentSet();
  const templateSelection = useTemplateSelection();
  // Gate the fetch on the caller's channel-ready signal so we don't fire
  // /api/v3/default-scenes against the UI-default channel on /scene/:id
  // routes before the scene reveals the real channel. Also defer it until
  // the panel is opened: this is the unfiltered channel-wide list — the
  // unbounded payload #502 trimmed for the editor's own fetch — and nothing
  // in the collapsed header needs it.
  const {overrides, isEndpointAvailable, endpointStatus} = useDefaultSceneOverrides(channel, {
    enabled: isChannelReady && expanded,
  });
  const setLocalOverride = useSetLocalOverride();

  // The scope starts at the product on screen, and goes back to it when the
  // product changes. "All products" was the start value, and was also forced
  // by a visit to the ALL view and kept after it: a save then wrote the row
  // every product inherits, while a product row with the same fields still
  // hid the change on the product on screen.
  const [scopeChoice, setScope] = useState("product");
  const [level, setLevel] = useState("set");
  // The stages a save goes to (stageTargets.ts): the stage on screen at the
  // start, and again when the stage or the product changes.
  const [checkedStages, setCheckedStages] = useState(() => [type.value]);
  const [bannerStatus, setBannerStatus] = useState(null);
  const bannerStatusTimerRef = useRef(null);
  const fieldGroups = getFieldGroups(channel.value);
  const availableFieldKeys = fieldGroups.flatMap((group) => group.fields);
  const [selectedFields, setSelectedFields] = useState(() => getAllFieldSelection(true, channel.value));
  const [status, setStatus] = useState(null);
  // The more specific rows the last save also wrote (see shadowingRows.ts).
  const [shadowWrites, setShadowWrites] = useState([]);
  // What the last save wrote to the background slot row, as the status tells it.
  const [savedSlotNote, setSavedSlotNote] = useState(null);
  const statusTimerRef = useRef(null);
  const isAllProductsMode = product.value === "ALL";

  // Undo: snapshot of the previous override state before last save
  const [undoPayload, setUndoPayload] = useState(null);
  // The banners the last save could not bake (bakeOnSave.ts).
  const [unbakedTemplates, setUnbakedTemplates] = useState([]);
  // {done, total} while a save cuts the backgrounds, after its other writes.
  const [bakeProgress, setBakeProgress] = useState(null);

  useEffect(
    () => () => {
      clearTimeout(statusTimerRef.current);
      clearTimeout(bannerStatusTimerRef.current);
    },
    []
  );

  // The ALL view has no product of its own to save for.
  const scope = isAllProductsMode ? "global" : scopeChoice;

  useEffect(() => {
    setScope("product");
  }, [product.value]);

  useEffect(() => {
    setCheckedStages([type.value]);
  }, [type.value, product.value]);

  // Field availability changes with the channel. Start each channel with all
  // of its fields selected, including Livestream's customBgs. Filtering the
  // previous channel's selection left that newly available field unchecked,
  // so Save omitted the edited gradient and the following refetch restored the
  // previous colors.
  useEffect(() => {
    setSelectedFields(getAllFieldSelection(true, channel.value));
  }, [channel.value]);

  const setStatusWithTimeout = (newStatus, ms) => {
    clearTimeout(statusTimerRef.current);
    setStatus(newStatus);
    statusTimerRef.current = setTimeout(() => setStatus(null), ms);
  };

  const toggleGroup = (groupFields, checked) => {
    setSelectedFields((prev) => {
      if (checked) {
        return Array.from(new Set([...prev, ...groupFields]));
      }
      return prev.filter((field) => !groupFields.includes(field));
    });
  };

  const toggleField = (field, checked) => {
    setSelectedFields((prev) => {
      if (checked) {
        return prev.includes(field) ? prev : [...prev, field];
      }
      return prev.filter((key) => key !== field);
    });
  };

  // The banners of the page, for the text colour (TextColorSection.tsx).
  const pageTemplateIds = useMemo(
    () =>
      getRouteTemplates(channel.value, {
        layoutOption,
        set: currentSet,
        product: product.value,
        type: type.value,
        hiddenTemplates,
        extraTemplates,
        selection: templateSelection,
      }).map((template) => template.id),
    [
      channel.value,
      layoutOption,
      currentSet,
      product.value,
      type.value,
      hiddenTemplates,
      extraTemplates,
      templateSelection,
    ]
  );

  const allFieldsSelected = availableFieldKeys.every((field) => selectedFields.includes(field));
  const someFieldsSelected = selectedFields.length > 0;

  const effectiveProduct = scope === "global" || isAllProductsMode ? "*" : product.value;
  const {data: channelSettings} = useChannelSettings(channel.value);
  const stageOptions = useMemo(
    () =>
      defaultStageOptions({
        channel: channel.value,
        product: effectiveProduct,
        settings: channelSettings,
      }),
    [channel.value, effectiveProduct, channelSettings]
  );
  const targetStages = resolveTargetStages(checkedStages, type.value, stageOptions);
  const writesCurrentStage = targetStages.includes(type.value);
  const otherStages = targetStages.filter((stage) => stage !== type.value);
  const stageLabel = (value) => stageOptions.find((option) => option.value === value)?.label ?? value;
  // The `type` a scene names, for a row of another stage.
  const stageTypeOf = (value) => ({label: stageLabel(value), value});
  const stagesLabel = stageOptions.length > 0 ? targetStages.map(stageLabel).join(" + ") : null;
  const currentSetLabel = currentSet || "default";
  const currentLayout = String(+layoutOption || 1);
  const currentBg = String(bgOption || "1");
  const backgroundNumbers = useBackgroundNumbers();
  const currentVariant = String(+variantOption || 1);
  // The treatments the design draws for the layout on screen. A product with
  // no treatment axis (every product but JetBrains Air) gets no variant level,
  // and neither does a layout the current treatment set does not cover.
  const layoutVariants = variantsForLayout(product.value, currentSet, currentLayout);
  const currentVariantLabel =
    layoutVariants?.find((item) => String(item.id) === currentVariant)?.label || null;
  // More than one treatment to tell apart, and the one on screen is among
  // them: a cell the design never drew is not a row the panel may write.
  const hasVariantLevels = (layoutVariants?.length ?? 0) > 1 && currentVariantLabel !== null;
  const levels = levelsForVariants(hasVariantLevels);
  const effectiveLayout =
    level === "layout" || level === "layout-background" || level === "layout-variant"
      ? currentLayout
      : null;
  const effectiveBg = level === "background" || level === "layout-background" ? currentBg : null;
  const effectiveVariant =
    level === "variant" || level === "layout-variant" ? currentVariant : null;

  // Leaving a treatment product (or a layout it draws no treatments for) must
  // not leave the panel pointed at a level it can no longer write: the Save
  // button would key a row by a variant the route does not have.
  useEffect(() => {
    if (!hasVariantLevels && VARIANT_LEVEL_VALUES.has(level)) {
      setLevel("set");
    }
  }, [hasVariantLevels, level]);

  // A crop describes ONE artwork, so it has to be written to the row it comes
  // from — a set-level crop sits under the per-background rows and never shows
  // (see cropRowTarget.ts). A banner's own image belongs to the background
  // number it is saved under, so it goes to that background's row, and the
  // crop goes with it (backgroundSlotTarget). Everything else still follows
  // the chosen level. ALL-products mode is left alone: its crops are
  // `<template>-<product>` instance keys resolved through a different path
  // (#567, R4). Livestream keeps one set of per-banner artworks per product
  // (its seeds), not one per background.
  const hasTemplateImages =
    !isAllProductsMode &&
    channel.value !== "livestream" &&
    isGalleryOption(bgOption) &&
    selectedFields.includes("customBgs") &&
    templateImageKeys(customBgs).length > 0;
  const slotTarget = useMemo(() => {
    if (isAllProductsMode) return null;
    const shadowCrop = CROP_FIELDS.some((field) => selectedFields.includes(field))
      ? shadowedCropTarget({
          overrides,
          channel: channel.value,
          product: product.value,
          type: type.value,
          set: currentSet,
          layout: currentLayout,
          background: currentBg,
          variant: hasVariantLevels ? currentVariant : null,
          chosenProduct: effectiveProduct,
          chosenLayout: effectiveLayout,
          chosenBackground: effectiveBg,
          chosenVariant: effectiveVariant,
        })
      : null;
    return backgroundSlotTarget({
      cropTarget: shadowCrop,
      hasTemplateImages,
      chosenBackground: effectiveBg,
      background: currentBg,
    });
  }, [
    isAllProductsMode,
    selectedFields,
    overrides,
    channel.value,
    product.value,
    type.value,
    currentSet,
    currentLayout,
    currentBg,
    effectiveProduct,
    effectiveLayout,
    effectiveBg,
    effectiveVariant,
    currentVariant,
    hasVariantLevels,
    hasTemplateImages,
  ]);
  // The brightness and the opacity of a gallery background belong to it, as
  // its crop does, and so do the colours of its banners: the base, the text
  // and the logos (lookRowTarget.ts). Only the colour fields the save writes
  // count. The ALL view and the custom slot keep them on the chosen row.
  const savedColours = useMemo(() => {
    const onScreen = {base, bases, textColorLocked, logoVariants};
    return Object.fromEntries(
      BG_COLOUR_FIELDS.filter((field) => selectedFields.includes(field)).map((field) => [
        field,
        onScreen[field],
      ])
    );
  }, [selectedFields, base, bases, textColorLocked, logoVariants]);
  const lookTarget = useMemo(
    () =>
      !isAllProductsMode && isGalleryOption(bgOption)
        ? lookRowTarget({
            overrides,
            channel: channel.value,
            product: product.value,
            type: type.value,
            set: currentSet,
            layout: currentLayout,
            background: currentBg,
            variant: hasVariantLevels ? currentVariant : null,
            look: {bgBrightness, bgOpacity},
            colours: savedColours,
            slot: slotTarget,
            chosenLayout: effectiveLayout,
            chosenBackground: effectiveBg,
            chosenVariant: effectiveVariant,
          })
        : null,
    [
      isAllProductsMode,
      bgOption,
      overrides,
      channel.value,
      product.value,
      type.value,
      currentSet,
      currentLayout,
      currentBg,
      hasVariantLevels,
      currentVariant,
      bgBrightness,
      bgOpacity,
      savedColours,
      slotTarget,
      effectiveLayout,
      effectiveBg,
      effectiveVariant,
    ]
  );
  // The second row of a save, the background slot: the crop and the banner
  // images, and the look. Null when everything goes to the chosen row.
  const cropTarget = slotTarget ?? lookTarget?.row ?? null;
  const slotTakesLook = Boolean(lookTarget?.row);
  // The fields the slot row takes from the save. A crop moves only with a
  // crop slot: without one, the chosen row wins for it.
  const slotFields = [
    ...(slotTarget ? CROP_PATCH_FIELDS : []),
    ...(slotTakesLook ? LOOK_ROW_FIELDS : []),
  ];
  // The banner images move with the crop only to a row of this background.
  const slotMovesImages = hasTemplateImages && !effectiveBg && Boolean(cropTarget?.background);
  // "opacity", "brightness and text colour": the look and colour fields the
  // slot row takes.
  const slotLookNames = slotTakesLook
    ? [...new Set(lookTarget.fields.map((field) => LOOK_ROW_FIELD_NAMES[field]))]
    : [];
  const slotLookLabel = slotTakesLook ? joinNames(slotLookNames) : null;
  // "background position to bg 2", "background opacity to bg 21".
  const slotNote = cropTarget
    ? `background ${[slotTarget && (slotMovesImages ? "images and position" : "position"), slotLookLabel]
        .filter(Boolean)
        .join(", ")} to ${cropTargetLabel(cropTarget)}`
    : null;

  /** A row of this channel and set, in the form the API calls take. */
  const rowOf = (stage, {product: rowProduct = effectiveProduct, layout, background, variant}) => ({
    channel: channel.value,
    product: rowProduct,
    type: stage,
    set: currentSet,
    layout: layout || undefined,
    background: background || undefined,
    variant: variant || undefined,
  });

  /**
   * The writes that give `patch` to the chosen row of `stage`, and to the rows
   * of that stage that would hide it there (shadowingRows.ts). Another stage
   * has no background axis: a background number names another image there.
   */
  const levelRowWrites = (stage, patch) => {
    const background = stage === type.value ? effectiveBg : null;
    const main = rowOf(stage, {layout: effectiveLayout, background, variant: effectiveVariant});
    const mainEntry = overrides.find((o) => matchesOverrideRow(o, main));
    const writes = [{row: main, sceneData: patch, previous: mainEntry?.scene_data || null}];
    // The ALL view resolves through per-product instance keys (see the save).
    if (isAllProductsMode) return writes;
    const shadows = shadowingRowPatches({
      overrides,
      channel: channel.value,
      product: product.value,
      type: stage,
      set: currentSet,
      layout: currentLayout,
      background: stage === type.value ? currentBg : null,
      variant: hasVariantLevels ? currentVariant : null,
      chosen: {
        product: effectiveProduct,
        layout: effectiveLayout,
        background,
        variant: effectiveVariant,
      },
      patch,
    });
    for (const shadow of shadows) {
      writes.push({
        row: rowOf(stage, shadow),
        sceneData: shadow.sceneData,
        previous: shadow.previous,
        shadow: {...shadow, stage},
      });
    }
    return writes;
  };

  /** One write; returns what the row holds after it, as far as the panel knows. */
  const writeRow = async ({row, sceneData, previous}) => {
    if (bgDerivation) {
      await API.patchDefaultScene({...row, sceneData});
      return sceneData;
    }
    const merged = mergeScenes([previous || {}, sceneData]) || sceneData;
    await API.setDefaultScene({...row, sceneData: merged});
    return merged;
  };

  // Snapshot the current override's scene_data before saving so we can undo.
  const snapshotForUndo = useCallback(() => {
    const mainEntry = overrides.find((o) =>
      matchesOverrideRow(o, {
        channel: channel.value,
        product: effectiveProduct,
        type: type.value,
        set: currentSet,
        layout: effectiveLayout,
        background: effectiveBg,
        variant: effectiveVariant,
      })
    );

    // The crop's own row, when it is not the one above (see cropRowTarget.ts).
    const cropEntry = cropTarget
      ? overrides.find((o) =>
          matchesOverrideRow(o, {
            channel: channel.value,
            product: effectiveProduct,
            type: type.value,
            set: currentSet,
            layout: cropTarget.layout,
            background: cropTarget.background,
            variant: cropTarget.variant,
          })
        )
      : null;

    const snapshot = {
      main: {
        channel: channel.value,
        product: effectiveProduct,
        type: type.value,
        set: currentSet,
        layout: effectiveLayout || undefined,
        background: effectiveBg || undefined,
        variant: effectiveVariant || undefined,
        sceneData: mainEntry?.scene_data || null,
      },
      crop: cropTarget
        ? {
            channel: channel.value,
            product: effectiveProduct,
            type: type.value,
            set: currentSet,
            layout: cropTarget.layout || undefined,
            background: cropTarget.background || undefined,
            variant: cropTarget.variant || undefined,
            sceneData: cropEntry?.scene_data || null,
          }
        : null,
      perProduct: [],
      shadows: [],
      baked: null,
      // The rows of the other checked stages (stageTargets.ts).
      stages: [],
    };

    // Snapshot per-product overrides in ALL mode (carousel bg selections)
    if (isAllProductsMode) {
      for (const [code, bg] of Object.entries(perProductBg)) {
        if (!bg) continue;
        const entry = overrides.find((o) =>
          matchesOverrideRow(o, {
            channel: channel.value,
            product: code,
            type: type.value,
            set: currentSet,
            layout: null,
            background: null,
            variant: null,
          })
        );
        snapshot.perProduct.push({
          channel: channel.value,
          product: code,
          type: type.value,
          set: currentSet,
          sceneData: entry?.scene_data || null,
        });
      }
    }

    return snapshot;
  }, [
    overrides,
    channel.value,
    effectiveProduct,
    type.value,
    currentSet,
    effectiveLayout,
    effectiveBg,
    effectiveVariant,
    cropTarget,
    isAllProductsMode,
    perProductBg,
  ]);

  const {mutate: saveAsDefault, isPending: isSavePending} = useMutation({
    mutationFn: async () => {
      const snapshot = snapshotForUndo();
      const existingSceneData = snapshot.main.sceneData || {};

      const fullScene = buildScene();
      const partialScene = pickFields(
        fullScene,
        selectedFields,
        visibilityCatalog(channel.value, templateSelection, existingSceneData)
      );
      const shouldSaveAllProductsBgOptions =
        isAllProductsMode && selectedFields.includes(ALL_PRODUCTS_BG_OPTIONS_FIELD);
      const allProductsBgOptions = shouldSaveAllProductsBgOptions
        ? Object.entries(perProductBg).reduce((acc, [code, bg]) => {
            if (!bg) return acc;
            acc[code] = String(bg);
            return acc;
          }, {})
        : null;
      const allProductsScenePatch = shouldSaveAllProductsBgOptions
        ? {[ALL_PRODUCTS_BG_OPTIONS_FIELD]: allProductsBgOptions || {}}
        : {};

      // The ALL view's banners show the look of their products' backgrounds,
      // which the products' rows keep (utils/allProductsScene.js).
      const patchData = isAllProductsMode
        ? withoutInstanceAppearance(
            {...partialScene, ...allProductsScenePatch},
            getReleaseProductCodesForType(type.value)
          )
        : {...partialScene, ...allProductsScenePatch};
      // The look and the colours of a gallery background are saved only when
      // the admin changed them (lookRowTarget.ts): unchanged, they would give
      // the look of this background to the others.
      if (lookTarget) {
        for (const field of LOOK_ROW_FIELDS) {
          if (!lookTarget.fields.includes(field)) delete patchData[field];
        }
      }
      // A crop save also names the banner the selector draws this background's
      // preview on: the horizontal banner whose crop changed last.
      if (!isAllProductsMode && CROP_FIELDS.some((field) => patchData[field] !== undefined)) {
        const routeTemplates = getRouteTemplates(channel.value, {
          layoutOption,
          set: currentSet,
          product: product.value,
          type: type.value,
          hiddenTemplates,
          extraTemplates,
          selection: templateSelection,
        });
        const previewTemplate = lastAdjustedCandidate(
          previewCandidates(routeTemplates, visible),
          bgAdjustmentOrder[String(bgOption)]
        );
        if (previewTemplate) patchData[BG_PREVIEW_TEMPLATE_FIELD] = previewTemplate.id;
      }

      // The other checked stages get the chosen row and the rows that hide it
      // there, without the fields bound to one background of this stage's
      // gallery (stageTargets.ts). Each row names its own stage. Collected
      // before any write, for Undo.
      const stagePatch = withoutBackgroundBoundFields(patchData);
      const hasStageFields = Object.keys(stagePatch).some((field) => !META_FIELDS.includes(field));
      const stageWrites = hasStageFields
        ? otherStages.flatMap((stage) =>
            levelRowWrites(
              stage,
              stagePatch.type === undefined ? stagePatch : {...stagePatch, type: stageTypeOf(stage)}
            )
          )
        : [];
      snapshot.stages = stageWrites.map(({row, previous}) => ({...row, sceneData: previous}));
      const writeOtherStages = async () => {
        for (const write of stageWrites) {
          write.sceneData = await writeRow(write);
        }
      };

      // Without the stage on screen there is no background row to write, and
      // so nothing to crop, bake or carry per product.
      if (!writesCurrentStage) {
        snapshot.main = null;
        snapshot.crop = null;
        snapshot.perProduct = [];
        await writeOtherStages();
        return {
          mergedScene: null,
          cropMerged: null,
          shadowMerged: [],
          bakeMerged: null,
          snapshot,
          bakeFailed: [],
          stageWrites,
        };
      }

      // Baked backgrounds (bakeOnSave.ts): a crop save cuts each positioned
      // banner to its box. A cut takes seconds, so the cuts come after every
      // other write: a page left during the cuts keeps the saved default, and
      // its banners show their crop until a save cuts them. The cuts go to a
      // row of this background. What to cut is read from the page now, before
      // the writes.
      const shouldBake =
        !isAllProductsMode &&
        BAKED_BG_CHANNELS.has(channel.value) &&
        isGalleryOption(bgOption) &&
        CROP_FIELDS.some((field) => patchData[field] !== undefined);
      const bakeAddress = shouldBake
        ? bakeRowAddress({
            cropTarget: slotTarget,
            chosen: {
              layout: effectiveLayout || null,
              background: effectiveBg || null,
              variant: effectiveVariant || null,
            },
            bgOption: String(bgOption),
          })
        : null;
      const bakeJobs = shouldBake
        ? collectBakeJobs(
            getRouteTemplates(channel.value, {
              layoutOption,
              set: currentSet,
              product: product.value,
              type: type.value,
              hiddenTemplates,
              extraTemplates,
              selection: templateSelection,
            })
              .map((template) => template.id)
              .filter((id) => visible.includes(id)),
            getBgOps
          )
        : [];
      const bakeRow =
        bakeAddress && bakeJobs.length > 0
          ? {
              channel: channel.value,
              product: effectiveProduct,
              type: type.value,
              set: currentSet,
              layout: bakeAddress.layout || undefined,
              background: bakeAddress.background || undefined,
              variant: bakeAddress.variant || undefined,
            }
          : null;
      const bakeRowPrevious = bakeRow
        ? overrides.find((o) =>
            matchesOverrideRow(o, {
              ...bakeRow,
              layout: bakeAddress.layout,
              background: bakeAddress.background,
              variant: bakeAddress.variant,
            })
          )?.scene_data || null
        : null;

      // The more specific rows of this route that carry a saved value would
      // still hide it, so the value goes to them too. The crop's own row gets
      // the crop through the crop write below. The ALL view resolves through
      // per-product instance keys and is left alone, as for the crop.
      const cropAddressKey = cropTarget
        ? [effectiveProduct, cropTarget.layout, cropTarget.background, cropTarget.variant]
            .map((value) => value || "")
            .join(":")
        : null;
      const shadows = isAllProductsMode
        ? []
        : shadowingRowPatches({
            overrides,
            channel: channel.value,
            product: product.value,
            type: type.value,
            set: currentSet,
            layout: currentLayout,
            background: currentBg,
            variant: hasVariantLevels ? currentVariant : null,
            chosen: {
              product: effectiveProduct,
              layout: effectiveLayout,
              background: effectiveBg,
              variant: effectiveVariant,
            },
            patch: patchData,
          })
            .map((shadow) => {
              const key = [shadow.product, shadow.layout, shadow.background, shadow.variant]
                .map((value) => value || "")
                .join(":");
              if (key !== cropAddressKey) return shadow;
              const sceneData = {...shadow.sceneData};
              for (const field of slotFields) delete sceneData[field];
              if (slotMovesImages) delete sceneData.customBgs;
              return {...shadow, sceneData, fields: Object.keys(sceneData)};
            })
            .filter((shadow) => shadow.fields.length > 0);
      snapshot.shadows = shadows.map((shadow) => ({
        channel: channel.value,
        product: shadow.product,
        type: type.value,
        set: currentSet,
        layout: shadow.layout || undefined,
        background: shadow.background || undefined,
        variant: shadow.variant || undefined,
        sceneData: shadow.previous,
      }));

      // Split the crop off: it goes to the row it resolves from, the rest to
      // the level the admin picked. The look of the background goes with it.
      // `cropTarget` is null whenever the chosen row already wins, and then
      // this is one write exactly as before.
      const cropPatch = {};
      const levelPatch = cropTarget ? {...patchData} : patchData;
      if (cropTarget) {
        for (const field of slotFields) {
          if (levelPatch[field] === undefined) continue;
          cropPatch[field] = levelPatch[field];
          delete levelPatch[field];
        }
        // The banner images go to the background's row; the shared slot
        // (`main`) stays on the chosen row. PATCH replaces the whole record,
        // so the background row keeps its own `main`.
        if (slotMovesImages && levelPatch.customBgs) {
          const images = {};
          const shared = {};
          for (const [key, value] of Object.entries(levelPatch.customBgs)) {
            (key === "main" ? shared : images)[key] = value;
          }
          const rowMain = snapshot.crop?.sceneData?.customBgs?.main;
          cropPatch.customBgs = rowMain === undefined ? images : {main: rowMain, ...images};
          if (Object.keys(shared).length > 0) levelPatch.customBgs = shared;
          else delete levelPatch.customBgs;
        }
      }

      // `mergedScene` is what onSuccess reports/patches into cache. With
      // bgDerivation, the SERVER merges (PATCH, 1d), so we send only the
      // selected fields and don't depend on a warm client cache; the reported
      // scene is the partial we sent. Legacy path merges client-side (PUT).
      let mergedScene;
      let cropMerged = null;
      if (bgDerivation) {
        mergedScene = levelPatch;
        await API.patchDefaultScene({
          channel: channel.value,
          product: effectiveProduct,
          type: type.value,
          set: currentSet,
          layout: effectiveLayout || undefined,
          background: effectiveBg || undefined,
          variant: effectiveVariant || undefined,
          sceneData: levelPatch,
        });
      } else {
        // MERGE new fields on top of existing override data so unselected
        // fields are preserved. mergeScenes deep-merges object fields per
        // sub-key; scalars and arrays are replaced.
        mergedScene = mergeScenes([existingSceneData, levelPatch]) || levelPatch;
        await API.setDefaultScene({
          channel: channel.value,
          product: effectiveProduct,
          type: type.value,
          set: currentSet,
          layout: effectiveLayout || undefined,
          background: effectiveBg || undefined,
          variant: effectiveVariant || undefined,
          sceneData: mergedScene,
        });
      }

      if (Object.keys(cropPatch).length > 0) {
        const cropRow = {
          channel: channel.value,
          product: effectiveProduct,
          type: type.value,
          set: currentSet,
          layout: cropTarget.layout || undefined,
          background: cropTarget.background || undefined,
          variant: cropTarget.variant || undefined,
        };
        if (bgDerivation) {
          cropMerged = cropPatch;
          await API.patchDefaultScene({...cropRow, sceneData: cropPatch});
        } else {
          cropMerged = mergeScenes([snapshot.crop?.sceneData || {}, cropPatch]) || cropPatch;
          await API.setDefaultScene({...cropRow, sceneData: cropMerged});
        }
      }

      const shadowMerged = [];
      for (const shadow of shadows) {
        const shadowRow = {
          channel: channel.value,
          product: shadow.product,
          type: type.value,
          set: currentSet,
          layout: shadow.layout || undefined,
          background: shadow.background || undefined,
          variant: shadow.variant || undefined,
        };
        if (bgDerivation) {
          await API.patchDefaultScene({...shadowRow, sceneData: shadow.sceneData});
          shadowMerged.push({shadow, sceneData: shadow.sceneData});
        } else {
          const merged = mergeScenes([shadow.previous, shadow.sceneData]) || shadow.sceneData;
          await API.setDefaultScene({...shadowRow, sceneData: merged});
          shadowMerged.push({shadow, sceneData: merged});
        }
      }

      // In ALL products mode, always save per-product bgOption from carousel
      // selections — this is independent of which field groups are checked.
      if (shouldSaveAllProductsBgOptions) {
        const perProductTasks = Object.entries(perProductBg)
          .filter(([, bg]) => bg)
          .map(([code, bg]) => {
            // Deep merge with existing per-product override
            const existingEntry = overrides.find((o) =>
              matchesOverrideRow(o, {
                channel: channel.value,
                product: code,
                type: type.value,
                set: currentSet,
                layout: null,
                background: null,
                variant: null,
              })
            );
            const existingData = existingEntry?.scene_data || {};
            const merged = mergeScenes([existingData, {bgOption: bg}]) || {bgOption: bg};
            return {
              code,
              promise: bgDerivation
                ? API.patchDefaultScene({
                    channel: channel.value,
                    product: code,
                    type: type.value,
                    set: currentSet,
                    sceneData: {bgOption: bg},
                  })
                : API.setDefaultScene({
                    channel: channel.value,
                    product: code,
                    type: type.value,
                    set: currentSet,
                    sceneData: merged,
                  }),
            };
          });

        const perProductResults = await Promise.allSettled(
          perProductTasks.map((task) => task.promise)
        );
        const failedCodes = perProductResults
          .map((result, index) =>
            result.status === "rejected" ? perProductTasks[index].code : null
          )
          .filter(Boolean);
        if (failedCodes.length > 0) {
          throw new Error(
            `[DefaultScenePanel] Failed to save per-product carousel defaults for: ${failedCodes.join(", ")}`
          );
        }
      }

      await writeOtherStages();

      // The cuts, last. Their images go onto the background row as the writes
      // above left it: each field is a whole record (a PATCH replaces it).
      let bakeMerged = null;
      let bakeFailed = [];
      if (bakeRow) {
        setBakeProgress({done: 0, total: bakeJobs.length});
        const bakeRun = await runBakeJobs(bakeJobs, (source) =>
          API.bakeBackground(source).finally(() =>
            setBakeProgress((progress) => progress && {...progress, done: progress.done + 1})
          )
        );
        bakeFailed = bakeRun.failed;
        if (Object.keys(bakeRun.results).length > 0) {
          const applyWrite = (base, patch) =>
            bgDerivation ? {...base, ...patch} : mergeScenes([base, patch]) || patch;
          const bakeKey = rowKey(bakeRow);
          let base = bakeRowPrevious || {};
          if (rowKey(snapshot.main) === bakeKey) base = applyWrite(base, levelPatch);
          if (snapshot.crop && rowKey(snapshot.crop) === bakeKey) {
            base = applyWrite(base, cropPatch);
          }
          for (const shadow of shadows) {
            if (rowKey(shadow) === bakeKey) base = applyWrite(base, shadow.sceneData);
          }
          const bakePatch = bakeRowPatch(base, bakeRun.results);
          bakeMerged = applyWrite(base, bakePatch);
          snapshot.baked = {...bakeRow, sceneData: bakeRowPrevious};
          if (bgDerivation) {
            await API.patchDefaultScene({...bakeRow, sceneData: bakePatch});
          } else {
            await API.setDefaultScene({...bakeRow, sceneData: bakeMerged});
          }
        }
      }

      return {
        mergedScene,
        cropMerged,
        shadowMerged,
        bakeMerged,
        snapshot,
        bakeFailed,
        stageWrites,
        slotNote: snapshot.crop ? slotNote : null,
      };
    },
    onSuccess: ({
      mergedScene,
      cropMerged,
      shadowMerged,
      bakeMerged,
      snapshot,
      bakeFailed,
      stageWrites,
      slotNote: writtenSlotNote,
    }) => {
      setUndoPayload(snapshot);
      // Read now: the look saved, the page no longer differs from the rows,
      // and the panel no longer names a slot row for it.
      setSavedSlotNote(writtenSlotNote);
      setUnbakedTemplates(bakeFailed);
      setShadowWrites([
        ...shadowMerged.map(({shadow}) => shadow),
        ...stageWrites.filter((write) => write.shadow).map((write) => write.shadow),
      ]);

      // With bgDerivation the server is the source of truth (PATCH merged the
      // row); converge by invalidating the v3 queries below instead of
      // hand-patching cache slices. setLocalOverride (and its per-product
      // mirror) is the legacy warm-cache patch — root cause 5 in #528 — so it's
      // skipped.
      if (!bgDerivation) {
        for (const {row, sceneData} of stageWrites) {
          setLocalOverride({...row, sceneData});
        }
      }
      if (!bgDerivation && snapshot.main) {
        setLocalOverride({
          channel: channel.value,
          product: effectiveProduct,
          type: type.value,
          set: currentSet,
          layout: effectiveLayout,
          background: effectiveBg,
          variant: effectiveVariant,
          sceneData: mergedScene,
        });

        for (const {shadow, sceneData} of shadowMerged) {
          setLocalOverride({
            channel: channel.value,
            product: shadow.product,
            type: type.value,
            set: currentSet,
            layout: shadow.layout,
            background: shadow.background,
            variant: shadow.variant,
            sceneData,
          });
        }

        if (cropMerged && snapshot.crop) {
          setLocalOverride({
            channel: channel.value,
            product: effectiveProduct,
            type: type.value,
            set: currentSet,
            layout: snapshot.crop.layout || null,
            background: snapshot.crop.background || null,
            variant: snapshot.crop.variant || null,
            sceneData: cropMerged,
          });
        }

        // After the others: the background row can be the chosen row, the
        // crop's row or a shadow, and the cuts were its last write.
        if (bakeMerged && snapshot.baked) {
          setLocalOverride({
            channel: channel.value,
            product: effectiveProduct,
            type: type.value,
            set: currentSet,
            layout: snapshot.baked.layout || null,
            background: snapshot.baked.background || null,
            variant: snapshot.baked.variant || null,
            sceneData: bakeMerged,
          });
        }

        if (isAllProductsMode && selectedFields.includes(ALL_PRODUCTS_BG_OPTIONS_FIELD)) {
          for (const [code, bg] of Object.entries(perProductBg)) {
            if (!bg) continue;
            const existingEntry = overrides.find((o) =>
              matchesOverrideRow(o, {
                channel: channel.value,
                product: code,
                type: type.value,
                set: currentSet,
                layout: null,
                background: null,
                variant: null,
              })
            );
            const existingData = existingEntry?.scene_data || {};
            const merged = mergeScenes([existingData, {bgOption: bg}]) || {bgOption: bg};
            setLocalOverride({
              channel: channel.value,
              product: code,
              type: type.value,
              set: currentSet,
              sceneData: merged,
            });
          }
        }
      }

      for (const queryKey of invalidatedQueryKeys(bgDerivation)) {
        queryClient.invalidateQueries({queryKey});
      }
      setStatusWithTimeout("saved", 2000);
    },
    onError: (err) => {
      console.error("[DefaultScenePanel] Save failed:", err);
      setStatusWithTimeout("save-failed", 3000);
    },
    onSettled: () => setBakeProgress(null),
  });

  const {mutate: undoSave, isPending: isUndoPending} = useMutation({
    mutationFn: async () => {
      if (!undoPayload) return;

      const {main, crop, perProduct, shadows, baked, stages} = undoPayload;

      // Restore main override (or delete if there was none). A save for other
      // stages only did not write it.
      if (main?.sceneData) {
        await API.setDefaultScene({...main});
      } else if (main) {
        await API.deleteDefaultScene({
          channel: main.channel,
          product: main.product,
          type: main.type,
          set: main.set,
          layout: main.layout,
          background: main.background,
          variant: main.variant,
        });
      }

      // Restore the rows of the other stages the save wrote.
      for (const entry of stages) {
        if (entry.sceneData) {
          await API.setDefaultScene(entry);
        } else {
          await API.deleteDefaultScene(entry);
        }
      }

      // Restore the crop's own row when the save went to a second one
      if (crop) {
        if (crop.sceneData) {
          await API.setDefaultScene({...crop});
        } else {
          await API.deleteDefaultScene({
            channel: crop.channel,
            product: crop.product,
            type: crop.type,
            set: crop.set,
            layout: crop.layout,
            background: crop.background,
            variant: crop.variant,
          });
        }
      }

      // Restore the more specific rows the save also wrote. They existed
      // before it (only rows that carry a field are written).
      for (const entry of shadows) {
        await API.setDefaultScene(entry);
      }

      // Restore the background row the baked images went to, unless one of
      // the restores above already put that row back.
      const restored = new Set([main, crop, ...shadows].filter(Boolean).map(rowKey));
      if (baked && !restored.has(rowKey(baked))) {
        if (baked.sceneData) {
          await API.setDefaultScene({...baked});
        } else {
          await API.deleteDefaultScene({
            channel: baked.channel,
            product: baked.product,
            type: baked.type,
            set: baked.set,
            layout: baked.layout,
            background: baked.background,
            variant: baked.variant,
          });
        }
      }

      // Restore per-product overrides
      for (const entry of perProduct) {
        if (entry.sceneData) {
          await API.setDefaultScene(entry);
        } else {
          await API.deleteDefaultScene({
            channel: entry.channel,
            product: entry.product,
            type: entry.type,
            set: entry.set,
          });
        }
      }
    },
    onSuccess: () => {
      setUndoPayload(null);
      setShadowWrites([]);
      for (const queryKey of invalidatedQueryKeys(bgDerivation)) {
        queryClient.invalidateQueries({queryKey});
      }
      setStatusWithTimeout("undone", 2000);
    },
    onError: (err) => {
      console.error("[DefaultScenePanel] Undo failed:", err);
      setStatusWithTimeout("undo-failed", 3000);
    },
  });

  const {mutate: promoteUpload, isPending: isPromotePending} = usePromoteUpload();
  const promoteUploadFirst = useCallback(
    (uploadId) =>
      promoteUpload(
        {uploadId, sessionUpload, shownAs: String(bgOption), set: currentSet},
        {
          onSuccess: ({variation, isShown}) => {
            // The page shows another background now: save nothing for this one.
            if (!isShown) return;
            // The image is the promoted background now; the custom slot keeps nothing.
            if (customUploadId) editCustomBg("");
            setPromotedTo(variation);
          },
          onError: (err) => {
            console.error("[DefaultScenePanel] Making the upload the first background failed:", err);
            setStatusWithTimeout("promote-failed", 4000);
          },
        }
      ),
    [promoteUpload, sessionUpload, bgOption, customUploadId, editCustomBg, currentSet]
  );

  // Save once the page shows the promoted background with its default scene
  // applied: every row key and the scene snapshot read the page state.
  useEffect(() => {
    if (promotedTo === null || String(bgOption) !== promotedTo || !isPageReadyToSave) return;
    setPromotedTo(null);
    saveAsDefault();
  }, [promotedTo, bgOption, isPageReadyToSave, saveAsDefault]);

  const showBannerStatus = (newStatus, ms) => {
    clearTimeout(bannerStatusTimerRef.current);
    setBannerStatus(newStatus);
    bannerStatusTimerRef.current = setTimeout(() => setBannerStatus(null), ms);
  };

  // The page's banners save at once, as the selector options above do: each
  // change writes both banner lists to the chosen row of every checked stage
  // (levelRowWrites). The scope runs the writes one after another, so the
  // last change is the last write.
  const {mutate: saveBanners} = useMutation({
    scope: {id: "default-scene-banners"},
    mutationFn: async (fields) => {
      const writes = targetStages.flatMap((stage) => levelRowWrites(stage, fields));
      for (const write of writes) {
        write.sceneData = await writeRow(write);
      }
      return writes;
    },
    onSuccess: (writes) => {
      // The snapshot of the last Save is older than these rows: its Undo
      // would put the old banners back.
      setUndoPayload(null);
      if (!bgDerivation) {
        for (const {row, sceneData} of writes) setLocalOverride({...row, sceneData});
      }
      queryClient.invalidateQueries({queryKey: ["default-scene-overrides"]});
      for (const queryKey of RESOLVED_DEFAULT_QUERY_KEYS) {
        queryClient.invalidateQueries({queryKey, refetchType: "none"});
      }
      showBannerStatus("saved", 2500);
    },
    onError: (err) => {
      console.error("[DefaultScenePanel] Saving the banners failed:", err);
      showBannerStatus("failed", 4000);
    },
  });

  const isPending = isSavePending || isUndoPending || isPromotePending;

  const handleSave = useCallback(() => {
    if (!isEndpointAvailable) {
      setStatusWithTimeout("endpoint-missing", 4000);
      return;
    }
    if (selectedFields.length === 0) {
      setStatusWithTimeout("no-fields", 2000);
      return;
    }
    // An image still uploading shows from a `blob:` URL that lives in this tab
    // only. Saved into a default, every other user would get no image.
    if (pendingUploadCount > 0) {
      setStatusWithTimeout("upload-pending", 3000);
      return;
    }
    if (bgIsDraftUpload && !sessionUpload) {
      throw new Error("Cannot save defaults for an unpublished background upload");
    }
    if (uploadToPromote) {
      // A gallery belongs to one product: "first for everyone" needs one.
      if (effectiveProduct === "*") {
        setStatusWithTimeout("upload-needs-product", 4000);
        return;
      }
      // The gallery is the one of the stage on screen.
      if (!writesCurrentStage) {
        setStatusWithTimeout("upload-needs-stage", 4000);
        return;
      }
      promoteUploadFirst(uploadToPromote);
      return;
    }
    saveAsDefault();
  }, [
    saveAsDefault,
    promoteUploadFirst,
    isEndpointAvailable,
    selectedFields,
    bgIsDraftUpload,
    sessionUpload,
    uploadToPromote,
    effectiveProduct,
    writesCurrentStage,
    pendingUploadCount,
  ]);

  const handleUndo = useCallback(() => {
    if (!undoPayload) return;
    undoSave();
  }, [undoPayload, undoSave]);

  const scopeLabel = scope === "global" ? "All products" : product.label;
  const setLabel = `Set ${shortenSetName(currentSetLabel)}`;
  const layoutLabel = `Layout ${currentLayout}`;
  // The tile number of the background, not its identity (libs/bgNumbering.ts).
  const currentBgNumber = backgroundLabel(currentBg, backgroundNumbers);
  const bgLabel = `Bg ${currentBgNumber}`;
  const layoutBgLabel = `Layout ${currentLayout} + Bg ${currentBgNumber}`;
  const variantLabel = `Variant ${currentVariant}`;
  const layoutVariantLabel = `Layout ${currentLayout} + Var ${currentVariant}`;
  // The treatment has a name in the design ("air logo"); the button has room
  // for the number alone, so the name goes in the tooltip.
  const variantName = currentVariantLabel ? ` (${currentVariantLabel})` : "";
  const LEVEL_TEXT = {
    set: {button: setLabel, title: "Save for the entire set", note: `set ${currentSetLabel}`},
    layout: {
      button: layoutLabel,
      title: `Save for current layout (${currentLayout})`,
      note: `layout ${currentLayout}`,
    },
    background: {
      button: bgLabel,
      title: `Save for current background (${currentBgNumber})`,
      note: `bg ${currentBg}`,
    },
    "layout-background": {
      button: layoutBgLabel,
      title: `Save for current layout (${currentLayout}) on current background (${currentBg}) only`,
      note: `layout ${currentLayout} + bg ${currentBg}`,
    },
    variant: {
      button: variantLabel,
      title: `Save for current brand treatment${variantName} on every layout`,
      note: `variant ${currentVariant}`,
    },
    "layout-variant": {
      button: layoutVariantLabel,
      title: `Save for this design cell only: layout ${currentLayout} in treatment ${currentVariant}${variantName}`,
      note: `layout ${currentLayout} + variant ${currentVariant}`,
    },
  };
  const levelLabel = LEVEL_TEXT[level].note;
  // A background number names another image in another stage's gallery.
  const backgroundLevelsBlocked = otherStages.length > 0;
  const stageBlockedByLevel = isBackgroundLevel(level);
  // Where a save goes: "IntelliJ IDEA · Beta + Release · set social-media".
  const saveTargetLabel = [scopeLabel, stagesLabel, levelLabel].filter(Boolean).join(" · ");
  const saveStagesSuffix =
    stagesLabel === null ? "" : targetStages.length === 1 ? ` · ${stagesLabel}` : ` · ${targetStages.length} stages`;
  const savesBackgroundBoundFields = selectedFields.some((field) =>
    BACKGROUND_BOUND_FIELDS.includes(field)
  );

  return (
    <>
      {expanded && (
        <>
          <span className="default-scene-warning">
            Danger zone: defaults affect multiple scenes
          </span>

          {uploadToPromote && (
            <span className="default-scene-warning default-scene-warning--bg-list">
              Saving makes this uploaded image the first background of{" "}
              {product.label ?? product.value} for everyone
              {currentSet ? `, in set ${currentSetLabel} only` : ""}.
            </span>
          )}
          {bgIsDraftUpload && !sessionUpload && (
            <span className="default-scene-warning default-scene-warning--bg-list">
              The selected background is an unpublished upload. Publish the list first
              ("Publish for everyone", in the Background section), then save defaults for it.
            </span>
          )}

          <div className="default-scene-controls">
            <div className="default-scene-control">
              <span className="default-scene-caption">Scope</span>
              <div className="default-scene-levels">
                <button
                  className={`default-scene-level ${scope === "global" ? "active" : ""}`}
                  onClick={() => setScope("global")}
                  title="Save for all products in this set"
                >
                  All products
                </button>
                {!isAllProductsMode && (
                  <button
                    className={`default-scene-level ${scope === "product" ? "active" : ""}`}
                    onClick={() => setScope("product")}
                    title={`Save only for ${product.label}`}
                  >
                    {product.label}
                  </button>
                )}
              </div>
            </div>
            <div className="default-scene-control">
              <span className="default-scene-caption">Level</span>
              <div className="default-scene-levels">
                {levels.map((item) => {
                  const isBlocked = backgroundLevelsBlocked && isBackgroundLevel(item.value);
                  return (
                    <button
                      key={item.value}
                      className={`default-scene-level ${level === item.value ? "active" : ""}`}
                      onClick={() => setLevel(item.value)}
                      disabled={isBlocked}
                      title={
                        isBlocked
                          ? `Only for ${stageLabel(type.value)}: each stage has its own backgrounds`
                          : LEVEL_TEXT[item.value].title
                      }
                    >
                      {LEVEL_TEXT[item.value].button}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* The stages a save goes to (stageTargets.ts). Releases only:
             * the other channels' types are formats, not stages. */}
            {stageOptions.length > 1 && (
              <div className="default-scene-control">
                <span
                  className="default-scene-caption"
                  title="The stages that Save and the banner changes go to. The stage on screen is checked at the start."
                >
                  Stage
                </span>
                <div className="default-scene-levels default-scene-stages">
                  {stageOptions.map((option) => {
                    const isCurrent = option.value === type.value;
                    const isBlocked = stageBlockedByLevel && !isCurrent;
                    return (
                      <label
                        key={option.value}
                        className={`default-scene-field default-scene-stage ${isBlocked ? "is-disabled" : ""}`}
                        title={
                          isBlocked
                            ? "A background level saves for the stage on screen only: each stage has its own backgrounds"
                            : isCurrent
                              ? "The stage on screen"
                              : `Save for ${option.label} too`
                        }
                      >
                        <input
                          type="checkbox"
                          value={option.value}
                          checked={targetStages.includes(option.value)}
                          disabled={isBlocked}
                          onChange={(e) =>
                            setCheckedStages(
                              e.target.checked
                                ? [...targetStages, option.value]
                                : targetStages.filter((stage) => stage !== option.value)
                            )
                          }
                        />
                        {option.label}
                      </label>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {otherStages.length > 0 && savesBackgroundBoundFields && (
            <span className="default-scene-note">
              {`${otherStages.map(stageLabel).join(" and ")} get${otherStages.length === 1 ? "s" : ""} the selected fields without the background, its position, look and colours, and the banner images: each stage has its own backgrounds.`}
            </span>
          )}

          {cropTarget && writesCurrentStage && (
            <span className="default-scene-note">
              {!slotTarget
                ? `Background ${slotLookLabel} save${slotLookNames.length === 1 ? "s" : ""} to ${cropTargetLabel(cropTarget)}: each background keeps its own.`
                : slotMovesImages
                  ? `Banner background images, with their position, zoom, turn and flip, save to ${cropTargetLabel(cropTarget)}: a banner's image belongs to the background it is saved under.`
                  : `Background position, zoom, turn and flip save to ${cropTargetLabel(cropTarget)} — the row they already come from. Saved for the whole set they would stay hidden under it.`}
              {slotTarget &&
                slotTakesLook &&
                ` The background ${slotLookLabel} go${slotLookNames.length === 1 ? "es" : ""} there too.`}
            </span>
          )}

          {/* The Stage dropdown's channel-wide order (releases: which of
           * EAP/Release/Beta/RC leads is a seasonal editorial choice). Only
           * releases has stages; other channels keep their declared order.
           * Shown only to the roles that the server lets save it. */}
          {channel.value === "releases" && features["defaults.stage-order"] && (
            <StageOrderSection channel={channel.value} />
          )}

          {/* The products, sets, layouts and background tiles the selectors
           * offer. Backgrounds are admin work; the other options superadmin. */}
          {(features["defaults.selector-visibility"] ||
            features["defaults.background-visibility"]) && (
            <SelectorVisibilitySection
              bgDisplayOrder={bgDisplayOrder}
              allBgUrls={allBgUrls}
              showsOptions={features["defaults.selector-visibility"]}
              showsBackgrounds={features["defaults.background-visibility"]}
            />
          )}

          {/* The banners of this page: removed ones, and on releases ones of
           * other sets. A default field, saved with the button below. */}
          <BannerCompositionSection
            onSave={saveBanners}
            status={bannerStatus}
            saveTargetLabel={saveTargetLabel}
            disabled={!isEndpointAvailable || !isRouteDefaultReady}
          />

          {/* A colour for the banner texts. It shows at once and saves with
           * the button below, as the textColorLocked field. */}
          <TextColorSection
            templateIds={pageTemplateIds}
            onChange={() => toggleField("textColorLocked", true)}
          />

          <div className="default-scene-groups">
            <label className="default-scene-select-all">
              <input
                type="checkbox"
                checked={allFieldsSelected}
                ref={(el) => {
                  if (el) el.indeterminate = someFieldsSelected && !allFieldsSelected;
                }}
                onChange={(e) => setSelectedFields(getAllFieldSelection(e.target.checked, channel.value))}
              />
              {allFieldsSelected ? "Deselect all" : "Select all"}
            </label>
            {fieldGroups.map((g) => (
              <div key={g.key} className="default-scene-group-wrap">
                <label className="default-scene-group" title={g.fields.join(", ")}>
                  <input
                    type="checkbox"
                    checked={g.fields.every((field) => selectedFields.includes(field))}
                    ref={(el) => {
                      if (!el) return;
                      const selectedCount = g.fields.filter((field) =>
                        selectedFields.includes(field)
                      ).length;
                      el.indeterminate = selectedCount > 0 && selectedCount < g.fields.length;
                    }}
                    onChange={(e) => toggleGroup(g.fields, e.target.checked)}
                  />
                  {g.label}
                </label>
                <div className="default-scene-fields">
                  {g.fields.map((field) => (
                    <label key={field} className="default-scene-field">
                      <input
                        type="checkbox"
                        checked={selectedFields.includes(field)}
                        onChange={(e) => toggleField(field, e.target.checked)}
                      />
                      {FIELD_LABELS[field] || field}
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="default-scene-row">
            {undoPayload && (
              <button
                className="default-scene-btn default-scene-undo"
                onClick={handleUndo}
                disabled={isPending}
                title="Undo last save"
              >
                {isUndoPending ? "..." : "Undo"}
              </button>
            )}
            <button
              className="default-scene-btn"
              onClick={handleSave}
              disabled={
                isPending ||
                !isPageReadyToSave ||
                !isEndpointAvailable ||
                selectedFields.length === 0 ||
                (bgIsDraftUpload && !sessionUpload)
              }
              title={
                isPageReadyToSave
                  ? `Save as default for ${saveTargetLabel}`
                  : "The page is still loading its default"
              }
            >
              {isSavePending || isPromotePending ? "..." : `Save for ${scopeLabel}${saveStagesSuffix}`}
            </button>
          </div>

          {isChannelReady && endpointStatus === "not-found" && (
            <span className="default-scene-status warning">
              Backend endpoint is unavailable (404)
            </span>
          )}
          {isChannelReady && endpointStatus === "unsupported" && (
            <span className="default-scene-status warning">
              Backend endpoint is not supported by upstream backend (compat mode)
            </span>
          )}
          {bakeProgress && (
            <span className="default-scene-status">
              Saved — cutting the backgrounds ({bakeProgress.done} of {bakeProgress.total}). Keep
              this page open.
            </span>
          )}
          {status === "saved" && (
            <span className="default-scene-status success">
              Saved
              {savedSlotNote ? ` — ${savedSlotNote}` : ""}
            </span>
          )}
          {status === "saved" && unbakedTemplates.length > 0 && (
            <span className="default-scene-note">
              Not baked, these banners keep their position: {unbakedTemplates.join(", ")}
            </span>
          )}
          {status === "saved" && shadowWrites.length > 0 && (
            <span className="default-scene-note">
              Also saved to the more specific rows that show on this banner:{" "}
              {shadowWrites
                .map(
                  (shadow) =>
                    `${shadow.stage ? `${stageLabel(shadow.stage)}: ` : ""}${rowAddressLabel(shadow)} (${shadow.fields.join(", ")})`
                )
                .join("; ")}
            </span>
          )}
          {status === "undone" && (
            <span className="default-scene-status success">Restored previous state</span>
          )}
          {status === "undo-failed" && (
            <span className="default-scene-status warning">Undo failed</span>
          )}
          {status === "promote-failed" && (
            <span className="default-scene-status warning">
              Couldn't make the upload a background for everyone — nothing was saved
            </span>
          )}
          {status === "upload-needs-product" && (
            <span className="default-scene-status warning">
              Choose one product: an uploaded background becomes part of that product's gallery
            </span>
          )}
          {status === "upload-needs-stage" && (
            <span className="default-scene-status warning">
              Check {stageLabel(type.value)}: an uploaded background becomes part of this stage's
              gallery
            </span>
          )}
          {status === "upload-pending" && (
            <span className="default-scene-status warning">
              Wait until the image upload finishes, then save
            </span>
          )}
          {status === "no-fields" && (
            <span className="default-scene-status warning">Select at least one parameter</span>
          )}
          {status === "endpoint-missing" && (
            <span className="default-scene-status warning">
              Cannot save: backend endpoint is unavailable
            </span>
          )}
          {status === "save-failed" && (
            <span className="default-scene-status warning">Save failed</span>
          )}

          {/* The position of this background, copied to the same picture of
           * the other products of the channel (replicateBg.ts). */}
          {!isAllProductsMode &&
            !bgIsDraftUpload &&
            isGalleryOption(bgOption) &&
            canReplicateOnChannel(channel.value) &&
            features["defaults.replicate-bg"] && (
              <ReplicateBgSection
                overrides={overrides}
                canWrite={isEndpointAvailable && isPageReadyToSave}
                backgroundNumber={currentBgNumber}
              />
            )}
        </>
      )}
    </>
  );
};

export default DefaultScenePanel;
